import React, { useState, useMemo } from 'react';
import { useWorkspace } from '../../../context/WorkspaceContext';
import { useAuth } from '../../../context/AuthContext';
import { Badge } from '../../../design-system/Badge';
import { Button } from '../../../design-system/Button';
import { Input } from '../../../design-system/Input';
import { Select } from '../../../design-system/Dropdown';
import { Modal } from '../../../design-system/Modal';
import {
  Search,
  Plus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Columns,
  Trash2,
  Layers,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

export const ListView = () => {
  const { user } = useAuth();
  const {
    tasks,
    setSelectedTask,
    setIsQuickAddOpen,
    addTask,
    updateTask,
    deleteTask,
    members = []
  } = useWorkspace();

  const [search, setSearch] = useState('');
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [sortField, setSortField] = useState('id'); // 'id', 'title', 'status', 'priority', 'dueDate', 'estimate'
  const [sortAsc, setSortAsc] = useState(true);
  const [groupBy, setGroupBy] = useState('none'); // 'none', 'status', 'priority', 'assignee'
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [inlineQuickTitle, setInlineQuickTitle] = useState('');
  const [isColumnPickerOpen, setIsColumnPickerOpen] = useState(false);

  // Column Visibility Configuration
  const [visibleColumns, setVisibleColumns] = useState({
    id: true,
    title: true,
    status: true,
    priority: true,
    assignee: true,
    estimate: true,
    dueDate: true,
    labels: true,
    subtasks: true
  });

  const availableColumns = [
    { id: 'id', label: 'Task ID / Key' },
    { id: 'title', label: 'Title & Specs' },
    { id: 'status', label: 'Status' },
    { id: 'priority', label: 'Priority' },
    { id: 'assignee', label: 'Assignee' },
    { id: 'estimate', label: 'Estimate' },
    { id: 'dueDate', label: 'Due Date' },
    { id: 'labels', label: 'Labels & Tags' },
    { id: 'subtasks', label: 'Subtasks & Checklists' }
  ];

  const statusOptions = [
    { value: 'backlog', label: 'Backlog' },
    { value: 'todo', label: 'To Do' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'review', label: 'Review' },
    { value: 'done', label: 'Done' }
  ];

  const priorityOptions = [
    { value: 'urgent', label: 'Urgent' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  // Filtering & Sorting
  const processedTasks = useMemo(() => {
    let result = tasks.filter(
      (t) =>
        t.title.toLowerCase().includes(search.toLowerCase()) ||
        t.id.toLowerCase().includes(search.toLowerCase()) ||
        (t.description || '').toLowerCase().includes(search.toLowerCase())
    );

    const priorityWeight = { urgent: 4, high: 3, medium: 2, low: 1 };
    const statusWeight = { backlog: 1, todo: 2, in_progress: 3, review: 4, done: 5 };

    result.sort((a, b) => {
      let valA = a[sortField] || '';
      let valB = b[sortField] || '';

      if (sortField === 'priority') {
        valA = priorityWeight[a.priority] || 0;
        valB = priorityWeight[b.priority] || 0;
      } else if (sortField === 'status') {
        valA = statusWeight[a.status] || 0;
        valB = statusWeight[b.status] || 0;
      } else if (sortField === 'assignee') {
        valA = typeof a.assignee === 'object' ? a.assignee?.name : a.assignee || '';
        valB = typeof b.assignee === 'object' ? b.assignee?.name : b.assignee || '';
      }

      if (valA < valB) return sortAsc ? -1 : 1;
      if (valA > valB) return sortAsc ? 1 : -1;
      return 0;
    });

    return result;
  }, [tasks, search, sortField, sortAsc]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  // Selection
  const allIds = processedTasks.map((t) => t.id);
  const isAllSelected = allIds.length > 0 && allIds.every((id) => selectedTaskIds.includes(id));

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedTaskIds([]);
    } else {
      setSelectedTaskIds(allIds);
    }
  };

  const handleToggleSelectRow = (taskId, e) => {
    e.stopPropagation();
    setSelectedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  // Bulk Actions
  const handleBulkStatusChange = (newStatus) => {
    selectedTaskIds.forEach((id) => updateTask(id, { status: newStatus }));
  };

  const handleBulkPriorityChange = (newPriority) => {
    selectedTaskIds.forEach((id) => updateTask(id, { priority: newPriority }));
  };

  const handleBulkAssign = (userName) => {
    const mem = members.find((m) => m.user.name === userName);
    const assigneeObj = mem ? { name: mem.user.name, avatar: mem.user.avatar } : { name: userName };
    selectedTaskIds.forEach((id) => updateTask(id, { assignee: assigneeObj }));
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Delete ${selectedTaskIds.length} selected tasks?`)) {
      selectedTaskIds.forEach((id) => deleteTask(id));
      setSelectedTaskIds([]);
    }
  };

  const handleInlineAdd = (e) => {
    e?.preventDefault();
    if (!inlineQuickTitle.trim()) return;
    const currentUserName = user?.name || 'Workspace Owner';
    const currentUserAvatar = user?.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(currentUserName)}`;

    addTask({
      title: inlineQuickTitle.trim(),
      status: 'todo',
      priority: 'medium',
      assignee: { name: currentUserName, avatar: currentUserAvatar }
    });
    setInlineQuickTitle('');
  };

  const toggleGroup = (groupKey) => {
    setCollapsedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown size={12} style={{ opacity: 0.35, marginLeft: '4px' }} />;
    return sortAsc ? (
      <ArrowUp size={12} style={{ color: 'var(--accent-teal)', marginLeft: '4px' }} />
    ) : (
      <ArrowDown size={12} style={{ color: 'var(--accent-teal)', marginLeft: '4px' }} />
    );
  };

  // Grouping helper
  const groupedSections = useMemo(() => {
    if (groupBy === 'none') {
      return [{ key: 'all', title: 'All Tasks', tasks: processedTasks }];
    }

    const groups = {};
    processedTasks.forEach((t) => {
      let gKey = t[groupBy] || 'Unassigned';
      if (groupBy === 'assignee') {
        gKey = typeof t.assignee === 'object' ? t.assignee?.name : t.assignee || 'Unassigned';
      }
      if (!groups[gKey]) groups[gKey] = [];
      groups[gKey].push(t);
    });

    return Object.keys(groups).map((k) => ({
      key: k,
      title: k.replace('_', ' ').toUpperCase(),
      tasks: groups[k]
    }));
  }, [processedTasks, groupBy]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} className="animate-fade-in">
      {/* List / Table Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, maxWidth: '480px' }}>
          <Input
            placeholder="Search list / table..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            icon={<Search size={15} />}
          />

          <div style={{ width: '150px' }}>
            <Select
              options={[
                { value: 'none', label: 'No Grouping' },
                { value: 'status', label: 'Group: Status' },
                { value: 'priority', label: 'Group: Priority' },
                { value: 'assignee', label: 'Group: Assignee' }
              ]}
              value={groupBy}
              onChange={setGroupBy}
              size="sm"
            />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={() => setIsColumnPickerOpen(true)}
            style={{
              padding: '7px 10px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--surface-recessed)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              fontWeight: '600'
            }}
            title="Choose visible table columns"
          >
            <Columns size={14} />
            <span>Columns</span>
          </button>

          <Button
            variant="primary"
            size="sm"
            icon={<Plus size={15} />}
            onClick={() => setIsQuickAddOpen(true)}
          >
            New Task
          </Button>
        </div>
      </div>

      {/* Inline Quick Add Row */}
      <form onSubmit={handleInlineAdd} style={{ display: 'flex', gap: '8px' }}>
        <Input
          placeholder="Type a new task title and press Enter to append..."
          value={inlineQuickTitle}
          onChange={(e) => setInlineQuickTitle(e.target.value)}
        />
        <Button variant="secondary" size="md" icon={<Plus size={15} />} type="submit">
          Add
        </Button>
      </form>

      {/* Table Data Grid Container */}
      <div
        style={{
          backgroundColor: 'var(--surface-card)',
          boxShadow: 'var(--shadow-raised)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-card)',
          overflowX: 'auto',
          position: 'relative'
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
          <thead>
            <tr
              style={{
                borderBottom: '1px solid var(--border-divider)',
                backgroundColor: 'var(--surface-alt)',
                color: 'var(--text-muted)',
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                userSelect: 'none'
              }}
            >
              <th style={{ padding: '12px 14px', width: '40px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleToggleSelectAll}
                  style={{ accentColor: 'var(--accent-teal)', cursor: 'pointer' }}
                />
              </th>

              {visibleColumns.id && (
                <th
                  onClick={() => handleSort('id')}
                  style={{ padding: '12px 14px', width: '90px', cursor: 'pointer' }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Key {renderSortIcon('id')}
                  </span>
                </th>
              )}

              {visibleColumns.title && (
                <th
                  onClick={() => handleSort('title')}
                  style={{ padding: '12px 14px', cursor: 'pointer' }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Title {renderSortIcon('title')}
                  </span>
                </th>
              )}

              {visibleColumns.status && (
                <th
                  onClick={() => handleSort('status')}
                  style={{ padding: '12px 14px', width: '120px', cursor: 'pointer' }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Status {renderSortIcon('status')}
                  </span>
                </th>
              )}

              {visibleColumns.priority && (
                <th
                  onClick={() => handleSort('priority')}
                  style={{ padding: '12px 14px', width: '110px', cursor: 'pointer' }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Priority {renderSortIcon('priority')}
                  </span>
                </th>
              )}

              {visibleColumns.assignee && (
                <th
                  onClick={() => handleSort('assignee')}
                  style={{ padding: '12px 14px', width: '150px', cursor: 'pointer' }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Assignee {renderSortIcon('assignee')}
                  </span>
                </th>
              )}

              {visibleColumns.estimate && (
                <th
                  onClick={() => handleSort('estimate')}
                  style={{ padding: '12px 14px', width: '90px', cursor: 'pointer' }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Estimate {renderSortIcon('estimate')}
                  </span>
                </th>
              )}

              {visibleColumns.dueDate && (
                <th
                  onClick={() => handleSort('dueDate')}
                  style={{ padding: '12px 14px', width: '110px', cursor: 'pointer' }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Due Date {renderSortIcon('dueDate')}
                  </span>
                </th>
              )}

              {visibleColumns.labels && (
                <th style={{ padding: '12px 14px', width: '140px' }}>Labels</th>
              )}

              {visibleColumns.subtasks && (
                <th style={{ padding: '12px 14px', width: '100px' }}>Subtasks</th>
              )}
            </tr>
          </thead>
          <tbody>
            {groupedSections.map((section) => {
              const isCollapsed = collapsedGroups[section.key];
              return (
                <React.Fragment key={section.key}>
                  {groupBy !== 'none' && (
                    <tr
                      onClick={() => toggleGroup(section.key)}
                      style={{
                        backgroundColor: 'var(--surface-recessed)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      <td colSpan={10} style={{ padding: '8px 14px', fontWeight: '800', fontSize: '12px', color: 'var(--text-primary)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                          <span>{section.title}</span>
                          <Badge variant="neutral" size="xs">{section.tasks.length}</Badge>
                        </div>
                      </td>
                    </tr>
                  )}

                  {!isCollapsed &&
                    section.tasks.map((t) => {
                      const isSelected = selectedTaskIds.includes(t.id);
                      const subtasksCount = (t.subtasks || []).length;
                      const subtasksDone = (t.subtasks || []).filter((s) => s.done).length;

                      return (
                        <tr
                          key={t.id}
                          onClick={() => setSelectedTask(t)}
                          style={{
                            borderBottom: '1px solid var(--border-divider)',
                            backgroundColor: isSelected ? 'rgba(35, 190, 179, 0.08)' : 'transparent',
                            cursor: 'pointer',
                            transition: 'background 0.12s'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--surface-hover)';
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => handleToggleSelectRow(t.id, e)}
                              style={{ accentColor: 'var(--accent-teal)', cursor: 'pointer' }}
                            />
                          </td>

                          {visibleColumns.id && (
                            <td style={{ padding: '10px 14px', fontWeight: '800', color: 'var(--text-muted)' }}>
                              {t.id}
                            </td>
                          )}

                          {visibleColumns.title && (
                            <td style={{ padding: '10px 14px' }}>
                              <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{t.title}</div>
                            </td>
                          )}

                          {visibleColumns.status && (
                            <td style={{ padding: '10px 14px' }} onClick={(e) => e.stopPropagation()}>
                              <select
                                value={t.status}
                                onChange={(e) => updateTask(t.id, { status: e.target.value })}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  outline: 'none',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}
                              >
                                <option value="backlog">Backlog</option>
                                <option value="todo">To Do</option>
                                <option value="in_progress">In Progress</option>
                                <option value="review">Review</option>
                                <option value="done">Done</option>
                              </select>
                            </td>
                          )}

                          {visibleColumns.priority && (
                            <td style={{ padding: '10px 14px' }} onClick={(e) => e.stopPropagation()}>
                              <select
                                value={t.priority}
                                onChange={(e) => updateTask(t.id, { priority: e.target.value })}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  outline: 'none',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}
                              >
                                <option value="urgent">Urgent</option>
                                <option value="high">High</option>
                                <option value="medium">Medium</option>
                                <option value="low">Low</option>
                              </select>
                            </td>
                          )}

                          {visibleColumns.assignee && (
                            <td style={{ padding: '10px 14px' }}>
                              {t.assignee ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <img
                                    src={t.assignee.avatar}
                                    alt={t.assignee.name}
                                    style={{ width: '20px', height: '20px', borderRadius: '999px', objectFit: 'cover' }}
                                  />
                                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                                    {t.assignee.name}
                                  </span>
                                </div>
                              ) : (
                                <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Unassigned</span>
                              )}
                            </td>
                          )}

                          {visibleColumns.estimate && (
                            <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: '12px' }}>
                              {t.estimate || '3 pts'}
                            </td>
                          )}

                          {visibleColumns.dueDate && (
                            <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: '12px' }}>
                              {t.dueDate || '—'}
                            </td>
                          )}

                          {visibleColumns.labels && (
                            <td style={{ padding: '10px 14px' }}>
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {(t.labels || []).slice(0, 2).map((lbl) => (
                                  <Badge key={lbl} size="xs" variant="recessed">
                                    {lbl}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                          )}

                          {visibleColumns.subtasks && (
                            <td style={{ padding: '10px 14px', fontSize: '12px', color: 'var(--text-muted)' }}>
                              {subtasksCount > 0 ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                  <Layers size={13} /> {subtasksDone}/{subtasksCount}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {processedTasks.length === 0 && (
          <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)' }}>
            No tasks found matching your filter criteria.
          </div>
        )}
      </div>

      {/* FLOATING BULK ACTION BAR */}
      {selectedTaskIds.length > 0 && (
        <div
          style={{
            position: 'sticky',
            bottom: '24px',
            margin: '0 auto',
            zIndex: 100,
            padding: '12px 20px',
            backgroundColor: 'var(--surface-card)',
            boxShadow: 'var(--shadow-raised-hover)',
            borderRadius: 'var(--radius-pill)',
            border: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}
          className="animate-slide-up"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Badge variant="teal" size="sm">
              {selectedTaskIds.length} Selected
            </Badge>
          </div>

          <div style={{ height: '16px', width: '1px', backgroundColor: 'var(--border-divider)' }} />

          {/* Bulk Status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Status:</span>
            <select
              onChange={(e) => e.target.value && handleBulkStatusChange(e.target.value)}
              defaultValue=""
              style={{
                padding: '4px 8px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-subtle)',
                backgroundColor: 'var(--surface-recessed)',
                color: 'var(--text-primary)',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              <option value="" disabled>Change...</option>
              {statusOptions.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* Bulk Priority */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Priority:</span>
            <select
              onChange={(e) => e.target.value && handleBulkPriorityChange(e.target.value)}
              defaultValue=""
              style={{
                padding: '4px 8px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-subtle)',
                backgroundColor: 'var(--surface-recessed)',
                color: 'var(--text-primary)',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              <option value="" disabled>Change...</option>
              {priorityOptions.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Bulk Assign */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Assign:</span>
            <select
              onChange={(e) => e.target.value && handleBulkAssign(e.target.value)}
              defaultValue=""
              style={{
                padding: '4px 8px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-subtle)',
                backgroundColor: 'var(--surface-recessed)',
                color: 'var(--text-primary)',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              <option value="" disabled>Member...</option>
              {members.map((m) => (
                <option key={m.user.id} value={m.user.name}>{m.user.name}</option>
              ))}
            </select>
          </div>

          <div style={{ height: '16px', width: '1px', backgroundColor: 'var(--border-divider)' }} />

          <Button variant="danger" size="xs" icon={<Trash2 size={13} />} onClick={handleBulkDelete}>
            Delete
          </Button>

          <Button variant="ghost" size="xs" onClick={() => setSelectedTaskIds([])}>
            Deselect
          </Button>
        </div>
      )}

      {/* COLUMN PICKER MODAL */}
      <Modal
        isOpen={isColumnPickerOpen}
        onClose={() => setIsColumnPickerOpen(false)}
        title="Customize Table Columns"
        subtitle="Show or hide attributes in your dense list view"
        footer={
          <Button variant="primary" onClick={() => setIsColumnPickerOpen(false)}>
            Apply Columns
          </Button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {availableColumns.map((col) => (
            <label
              key={col.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                backgroundColor: 'var(--surface-recessed)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer'
              }}
            >
              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                {col.label}
              </span>
              <input
                type="checkbox"
                checked={visibleColumns[col.id]}
                onChange={() =>
                  setVisibleColumns((prev) => ({ ...prev, [col.id]: !prev[col.id] }))
                }
                style={{ accentColor: 'var(--accent-teal)', cursor: 'pointer' }}
              />
            </label>
          ))}
        </div>
      </Modal>
    </div>
  );
};
